import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Lock, CheckCircle2, KeyRound } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { authProbeSetPasswordToken, authSetPassword } from '../services/api';

const ACCESS_TOKEN_KEY = 'oc_access_token';

const SetPasswordPage = () => {
  useDocumentMeta({ title: 'Jelszó beállítása | Fiókom | Orosz Coaching' });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [status, setStatus] = useState('checking'); // checking | ok | invalid
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    (async () => {
      try {
        const { email, name } = await authProbeSetPasswordToken(token);
        setEmail(email);
        setName(name || '');
        setStatus('ok');
      } catch (err) {
        setStatus('invalid');
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('A jelszónak legalább 8 karakternek kell lennie.');
      return;
    }
    if (password !== confirm) {
      toast.error('A két jelszó nem egyezik.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authSetPassword(token, password);
      localStorage.setItem(ACCESS_TOKEN_KEY, res.access_token);
      toast.success('Jelszó beállítva. Bejelentkeztünk téged.');
      navigate('/fiokom', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A jelszó beállítása sikertelen.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleInstead = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/fiokom/bejelentkezes';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-16 md:py-24 bg-gradient-to-br from-white to-slate-100">
        <div className="w-full max-w-md" data-testid="setpw-card">
          {status === 'checking' && (
            <div className="text-center py-24">
              <Loader2 className="animate-spin text-yellow-600 mx-auto mb-3" size={32} />
              <p className="text-gray-600">Link ellenőrzése…</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="text-center py-16">
              <div className="inline-flex w-14 h-14 rounded-full bg-red-100 items-center justify-center mb-4">
                <KeyRound className="text-red-600" size={24} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Érvénytelen vagy lejárt link
              </h1>
              <p className="text-gray-600 mb-6">
                Ez a jelszó-beállító link már fel lett használva vagy lejárt (72 óra után).
              </p>
              <Button
                onClick={() => navigate('/fiokom/bejelentkezes')}
                className="bg-gray-900 hover:bg-yellow-600 text-white"
              >
                Vissza a bejelentkezéshez
              </Button>
            </div>
          )}

          {status === 'ok' && (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex w-14 h-14 rounded-full bg-yellow-100 items-center justify-center mb-3">
                  <CheckCircle2 className="text-yellow-700" size={26} />
                </div>
                <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Fiók aktiválása
                </h1>
                <p className="text-sm text-gray-600 mt-2">
                  Szia {name || email}! Válassz jelszót — vagy egy kattintással Google-lel is aktiválhatod:
                </p>
              </div>

              <button
                data-testid="setpw-google-instead"
                onClick={handleGoogleInstead}
                className="w-full h-12 flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-yellow-500 hover:shadow-md rounded-xl font-semibold text-gray-900 transition-all mb-5"
              >
                <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.72 1.22 9.22 3.62l6.86-6.86C35.9 2.06 30.29 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.45 13.5 17.7 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.5c0-1.57-.15-3.09-.42-4.5H24v9h12.94c-.58 3.13-2.29 5.79-4.87 7.58l7.73 6c4.51-4.16 7.18-10.29 7.18-18.08z"/>
                  <path fill="#FBBC05" d="M10.54 28.59a14.52 14.52 0 0 1 0-9.18l-7.98-6.19a24.02 24.02 0 0 0 0 21.56l7.98-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.14 15.9-5.83l-7.73-6c-2.14 1.44-4.9 2.28-8.17 2.28-6.3 0-11.64-4.24-13.55-9.94l-7.98 6.19C6.49 42.62 14.61 48 24 48z"/>
                </svg>
                <span className="text-sm">Fiók összekötése Google-lel</span>
              </button>

              <div className="my-4 flex items-center gap-3 text-xs uppercase tracking-widest text-gray-400">
                <span className="flex-1 h-px bg-gray-200" />
                vagy állíts be jelszót
                <span className="flex-1 h-px bg-gray-200" />
              </div>

              <form onSubmit={handleSubmit} data-testid="setpw-form" className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-700">Új jelszó</Label>
                  <Input
                    data-testid="setpw-password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 mt-1.5"
                    placeholder="Legalább 8 karakter"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-700">Jelszó megerősítése</Label>
                  <Input
                    data-testid="setpw-confirm"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="h-11 mt-1.5"
                  />
                </div>
                <Button
                  data-testid="setpw-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-gray-900 hover:bg-yellow-600 text-white font-bold"
                >
                  {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> Beállítás…</> : <><Lock size={14} className="mr-2" /> Jelszó beállítása</>}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SetPasswordPage;
